import { useEffect, useState } from "react";

import {
  createDefaultValues,
  createSchema,
} from "../../entities/recipients/forms/SwiftDestination.form";
import {
  useFetchSwiftAdditionalDetails,
  useFetchSwiftAdditionalFieldsByCountry,
} from "../../entities/recipients/requests";

/**
 * Whether a saved international wire destination fails the form's validation,
 * so the drawer can open the form before the user does. The fields a bank asks
 * for come from a lookup, so they're looked up the same way the form does: by
 * the BIC, or with a correspondent bank, by the beneficiary bank's country.
 */
export const useIsSwiftDestinationIncomplete = (
  savedDestination?: API.SwiftDestination | null,
) => {
  const { fetchSwiftAdditionalDetails } = useFetchSwiftAdditionalDetails();
  const { fetchSwiftAdditionalFieldsByCountry } =
    useFetchSwiftAdditionalFieldsByCountry();
  const [isIncomplete, setIsIncomplete] = useState(false);

  useEffect(() => {
    setIsIncomplete(false);
    if (!savedDestination) return undefined;
    let isCurrent = true;

    const lookUpBankFields = async (): Promise<
      | API.SwiftAdditionalDetails
      | API.FetchSwiftAdditionalFieldsByCountry.Response
      | null
      | void
    > => {
      const correspondentBic = savedDestination.correspondent_bank_bic;
      const countryCode = savedDestination.branch_address?.country_code;
      try {
        if (correspondentBic) {
          return countryCode
            ? await fetchSwiftAdditionalFieldsByCountry(countryCode)
            : null;
        }
        return savedDestination.bic
          ? await fetchSwiftAdditionalDetails(savedDestination.bic)
          : null;
      } catch {
        // if the lookup fails we don't know what this bank asks for, so only check
        // the fields every bank needs, like the form does
        return null;
      }
    };

    const check = async () => {
      const bankFields = await lookUpBankFields();
      if (!isCurrent) return;
      const additionalFields = bankFields?.additional_fields || [];
      const fieldNames = new Set(additionalFields.map((field) => field.name));
      // the form fills in the routing number and branch address from the BIC lookup
      // before it validates, so do the same here
      const bicDetails =
        bankFields && "institution_name" in bankFields ? bankFields : null;
      const branchAddress =
        savedDestination.branch_address ||
        (fieldNames.has("branch_address") ? bicDetails?.address : undefined);
      const routingNumber =
        savedDestination.metadata?.routing_number ||
        (fieldNames.has("routing_number")
          ? bicDetails?.swift_routing_code
          : undefined);
      const values = {
        ...createDefaultValues(),
        ...savedDestination,
        branch_address: branchAddress,
        metadata: {
          ...savedDestination.metadata,
          routing_number: routingNumber,
        },
        correspondent_bank_bic: savedDestination.correspondent_bank_bic ?? "",
        isCorrespondentBankEnabled: Boolean(
          savedDestination.correspondent_bank_bic,
        ),
        renameFields: bankFields?.rename_fields || {},
        additionalFieldsToShow: Object.fromEntries(
          additionalFields.map((field) => [field.name, true]),
        ),
      };
      setIsIncomplete(!createSchema().safeParse(values).success);
    };
    check();
    return () => {
      isCurrent = false;
    };
    // only when the saved destination changes, not on every render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savedDestination?.id]);

  return isIncomplete;
};
